// NewTicket.tsx
import React, { useState, useCallback, useRef } from 'react';
import SummernoteCorgee from '@components/Globals/SummernoteCorgee';
import FileUpload, { type FileUploadRef } from '@components/Globals/FileUpload';
import { newTicketService } from '@services/NewTicketService';
import { Modal } from '../utils/Modal';
import './NewTicket.css';
import ProjectsEditable from './ProjectsEditable';
import { MAX_BODY_BYTES, BODY_TOO_LARGE_MESSAGE } from '@lib/constants';
import { byteLength } from '@lib/utils';

// ==================== TYPES ====================

interface ApiConfig {
  url: string;
  token: string;
}

interface Layer {
  Color: string;
  [key: string]: any;
}

interface ConfigValue {
  Icon: string;
  [key: string]: any;
}

interface Tag {
  PersonId: number;
  ConversationId: number;
  ReplyId: number;
  ReplyFollowing: number;
}

interface Attachment {
  [key: string]: any;
}

interface Conversation {
  ConversationId: number;
  ConfigValueId: number;
  Title: string;
  Body: string;
  body?: string;
  IsRedFlag: boolean;
  Tags?: Tag[];
  Attachments?: Attachment[];
  [key: string]: any;
}

interface Config {
  apiUrl: string;
  authToken: string;
  params: {
    SupportLayer: Layer;
    SupportConfigValue: ConfigValue;
    SupportProjectId: number;
    Conversation: Conversation;
  };
  functions: {
    conversationResponse?: (data: any) => void;
  };
}

interface NewTicketProps {
  config: Config;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface RequestConfig {
  api: ApiConfig;
  request?: any;
}

// ==================== MAIN COMPONENT ====================

const NewTicket: React.FC<NewTicketProps> = ({ config, open, setOpen }) =>
{
  // State management
  const [conversation, setConversation] = useState<Conversation>(config.params.Conversation);
  const [showTitleWarning, setShowTitleWarning] = useState(false);
  // Save failures that aren't title related
  const [saveWarning, setSaveWarning] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // Refs
  const corgeeModalRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileUploadRef = useRef<FileUploadRef>(null);
  const summernoteRef = useRef<any>(null);

  const onDropFiles = useCallback((files: File[]) =>
  {
    fileUploadRef.current?.onDrop(files);
  }, []);

  // Constants from props
  const supportLayer = config.params.SupportLayer;
  const supportConfigValue = config.params.SupportConfigValue;
  const emptyConversation = config.params.Conversation;
  const projectId = config.params.SupportProjectId;
  const configValueId = config.params.Conversation.ConfigValueId;
  const api: ApiConfig = {
    url: config.apiUrl,
    token: config.authToken,
  };

  // Extract tags from body HTML
  const getTagsInBody = useCallback((bodyTags: string): Tag[] =>
  {
    try
    {
      const taggedPeople = $('<div>' + bodyTags + '</div>').find('span[person-id]');
      const listTags: Tag[] = [];

      for (let i = 0; i < taggedPeople.length; i++)
      {
        listTags.push({
          PersonId: parseInt(($(taggedPeople[i]!) as any).attr('person-id') || '0'),
          ConversationId: 0,
          ReplyId: 0,
          ReplyFollowing: 0,
        });
      }

      return listTags;
    }
    catch (err)
    {
      console.warn(err);
      return [];
    }
  }, []);

  // Get request configuration
  const getRequestConfig = useCallback(
    (request?: any): RequestConfig =>
    {
      return {
        api: {
          url: config.apiUrl,
          token: '',
        },
        request: request,
      };
    },
    [config.apiUrl],
  );

  // Save new ticket
  const onSaveNewTicket = useCallback(async () =>
  {
    if (isSaving || isUploadingFile)
    {
      return;
    }

    setIsSaving(true);
    setSaveWarning(null);

    const newConv = { ...conversation };

    // The editor propagates its content debounced; read it directly on save.
    const liveBody = summernoteRef.current?.getContent?.();
    if (typeof liveBody === 'string') newConv.Body = liveBody;

    // Validate title
    if (newConv.Title.trim() === '')
    {
      setShowTitleWarning(true);
      setIsSaving(false);
      return;
    }

    // Check body size
    if (byteLength(newConv.Body) > MAX_BODY_BYTES)
    {
      setSaveWarning(BODY_TOO_LARGE_MESSAGE);
      setIsSaving(false);
      return;
    }

    // Handle empty body
    if (newConv.Body === '<p><br></p>')
    {
      newConv.Body = '<span></span>';
    }

    // Extract tags from body
    newConv.Tags = getTagsInBody(newConv.Body);

    const requestConfig = getRequestConfig();
    requestConfig.request = { ...newConv };

    try
    {
      const dataResponse = await newTicketService.PostConversationOtherTenant(newConv as any);
      setOpen(false);

      // Call response callback
      if (config.functions?.conversationResponse)
      {
        config.functions.conversationResponse(dataResponse);
      }

      // Reset state
      setIsSaving(false);
      setConversation({ ...emptyConversation });
    }
    catch (error)
    {
      console.warn(error);
      // Keep what was typed so the save can be retried
      setSaveWarning('The ticket could not be saved. Please try again.');
      setIsSaving(false);
    }
  }, [
    isSaving,
    isUploadingFile,
    conversation,
    getTagsInBody,
    getRequestConfig,
    config.functions,
    emptyConversation,
  ]);

  // Open modal
  const _openModal = useCallback(() =>
  {
    // Reset warnings
    setShowTitleWarning(false);
    setSaveWarning(null);
    setIsSaving(false);

    if (corgeeModalRef.current)
    {
      corgeeModalRef.current.openModal(() =>
      {
        // Set font name
        const element = $('.react-summernote-new-ticket').find('.note-current-fontname');
        if (element.length > 0)
        {
          element.text('Roboto');
        }

        // Focus input
        if (inputRef.current)
        {
          inputRef.current.focus();
        }
      });
    }
  }, []);

  // Close modal
  const _closeModal = useCallback(() =>
  {
    if (corgeeModalRef.current)
    {
      corgeeModalRef.current.closeModal();
      setConversation({ ...emptyConversation });
    }
  }, [emptyConversation]);
  void _openModal;
  void _closeModal;

  // Handle file upload change
  const onChangeFileUpload = useCallback((filesUploaded: Attachment[] | boolean) =>
  {
    if (filesUploaded === true)
    {
      setIsUploadingFile(true);
    }
    else
    {
      setConversation(prev => ({
        ...prev,
        Attachments: filesUploaded as Attachment[],
      }));
      setIsUploadingFile(false);
    }
  }, []);

  // Toggle red flag
  const onMarkAsRedFlag = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) =>
    {
      e.preventDefault();
      setConversation(prev => ({
        ...prev,
        IsRedFlag: !prev.IsRedFlag,
      }));
    },
    [conversation],
  );

  // Update title
  const updateTitle = useCallback((e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const newTitle = e.target.value;
    setConversation(prev => ({
      ...prev,
      Title: newTitle,
    }));

    setShowTitleWarning(newTitle.trim() === '');
  }, []);

  // Handle summernote content change
  const onChangeSummernote = useCallback((content: string) =>
  {
    const newContent = content.replace('&amp;', '&');
    setConversation(prev => ({
      ...prev,
      Body: newContent,
    }));
  }, []);

  // CSS classes and tooltip text
  const cssClass = {
    RedFlag: conversation.IsRedFlag
      ? 'corgee-icon-red-flag enable-help-tooltip red'
      : 'corgee-icon-red-flag enable-help-tooltip',
    TaskTitle: conversation.IsRedFlag
      ? 'task-title enable-help-tooltip red'
      : 'task-title enable-help-tooltip',
  };

  const tooltipText = {
    RedFlag: conversation.IsRedFlag ? 'Unmark as Redflag' : 'Mark as Redflag',
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)} title="New Ticket for Support Team">
      <div className="w-[550px]">
        <div className="react-corgee-new_ticket-modal">
          {/* Projects Editable */}
          <div>
            <ProjectsEditable
              isForTicket={true}
              conversation={conversation as any}
              config={config as any}
            />
          </div>

          {/* Title Line */}
          <div className="react-corgee-new_ticket_title-line">
            <div className="react-corgee-item-icons">
              <span className={supportConfigValue.Icon}></span>

              <div className="title-card-action-icon red-flag-card">
                <span
                  className={cssClass.RedFlag}
                  data-title={tooltipText.RedFlag}
                  onClick={onMarkAsRedFlag}
                ></span>
              </div>
            </div>

            <div className="react-corgee-item-title-input">
              <input
                className="react-corgee-new_ticket_title"
                onChange={updateTitle}
                type="text"
                placeholder="Type title here"
                ref={inputRef}
                value={conversation.Title}
              />
              {showTitleWarning && (
                <div className="react-corgee-warning">
                  <p>Enter a valid Title to this Ticket.</p>
                </div>
              )}
              {saveWarning && (
                <div className="react-corgee-warning">
                  <p>{saveWarning}</p>
                </div>
              )}
            </div>
          </div>

          {/* Summernote Editor */}
          <div className="react-summernote-new-ticket">
            <div className="card-content-container-new-ticket">
              <SummernoteCorgee
                ref={summernoteRef}
                conversationId={conversation.ConversationId}
                onChangeBody={onChangeSummernote}
                body={conversation.body ?? ''}
                layerColor={supportLayer.Color}
                textHeight={10}
                layers={[supportLayer] as any}
                api={api}
                projectId={projectId}
                configValueId={configValueId}
                selectorContainer=".card-content-container-new-ticket"
                isAddReply={false}
                fixedDropDownPosition={false}
                onDropFiles={onDropFiles}
              />

              {/* File Upload */}
              <div className="corgee-uploader" style={{ borderColor: supportLayer.Color }}>
                <FileUpload ref={fileUploadRef} onChangeFileUpload={onChangeFileUpload as any} config={api} />
              </div>
            </div>
          </div>
        </div>
        <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
          <button
            type="button"
            style={{ backgroundColor: supportLayer.Color }}
            onClick={() =>
            {
              onSaveNewTicket();
            }}
            className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1 dark:border-[#333535]"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1 dark:bg-[#5c6060] dark:border-[#5c6060]"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default NewTicket;
